import { useMemo } from 'react';
import { TrendingUp } from 'lucide-react';
import { Bar, BarChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useI18n } from '../../../contexts/I18nContext';
import { usePersonPayroll } from '../../../hooks/useEmployment';
import { formatCurrency } from '../../../lib/format';
import { CHART_TOOLTIP_STYLE } from '../../../lib/chartTheme';

// ---------------------------------------------------------------------------
// Per-person financial dashboard ("Angajare" tab): how much the company pays
// this team member each month. A summary row (total over the charted window,
// monthly average, last month) plus a stacked bar chart per month breaking the
// total cost into net salary + employee contributions + income tax + employer
// CAM — the taxes breakdown requested for CIM employees. Data comes from the
// same react-query cache as the payroll history table (usePersonPayroll), so
// no extra requests are made. Renders nothing until at least one payroll entry
// exists; loading/error/empty states stay with the table below.
// ---------------------------------------------------------------------------

const SERIES = [
  { key: 'net', color: '#10B981', labelKey: 'people.payroll.net' },
  { key: 'contributions', color: '#3B82F6', labelKey: 'people.payroll.employeeContributions' },
  { key: 'incomeTax', color: '#8B5CF6', labelKey: 'people.payroll.incomeTax' },
  { key: 'cam', color: '#F59E0B', labelKey: 'people.payroll.cam' },
] as const;

const MONTHS_SHOWN = 12;

export function PayrollDashboard({ personId }: { personId: string }) {
  const { t } = useI18n();
  const { data: entries } = usePersonPayroll(personId);

  const model = useMemo(() => {
    if (!entries || entries.length === 0) return null;
    // API returns newest-first; chart wants chronological order.
    const chronological = [...entries].sort((a, b) => a.year - b.year || a.month - b.month);
    const window = chronological.slice(-MONTHS_SHOWN);
    const currency = window[window.length - 1].currency || 'RON';

    const chart = window.map((e) => ({
      label: `${String(e.month).padStart(2, '0')}/${e.year}`,
      net: e.netSalary,
      contributions: e.employeeContributions,
      incomeTax: e.incomeTax,
      cam: e.employerCam,
      total: e.totalCost,
    }));

    const total = window.reduce((sum, e) => sum + e.totalCost, 0);
    const last = window[window.length - 1];
    return {
      chart,
      currency,
      monthCount: window.length,
      total,
      average: total / window.length,
      last: {
        label: `${String(last.month).padStart(2, '0')}/${last.year}`,
        totalCost: last.totalCost,
        net: last.netSalary,
        taxes: last.totalCost - last.netSalary,
      },
    };
  }, [entries]);

  if (!model) return null;

  const money = (v: number) => formatCurrency(v, model.currency);
  const seriesLabel = (key: string) =>
    t(SERIES.find((s) => s.key === key)?.labelKey ?? 'people.payroll.totalCost');

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-1 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-brand-600" aria-hidden />
        <h2 className="text-sm font-semibold text-gray-900">{t('people.payroll.dash.title')}</h2>
      </div>
      <p className="text-xs text-gray-500">
        {t('people.payroll.dash.subtitle', { count: model.monthCount })}
      </p>

      {/* Summary row */}
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <SummaryCard
          label={t('people.payroll.dash.totalPaid', { count: model.monthCount })}
          value={money(model.total)}
        />
        <SummaryCard label={t('people.payroll.dash.avgMonthly')} value={money(model.average)} />
        <SummaryCard
          label={`${t('people.payroll.dash.lastMonth')} · ${model.last.label}`}
          value={money(model.last.totalCost)}
          detail={t('people.payroll.dash.lastMonthDetail', {
            net: money(model.last.net),
            taxes: money(model.last.taxes),
          })}
        />
      </div>

      {/* Monthly stacked cost chart (net + taxes = total cost) */}
      <div className="mt-4">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={model.chart} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            <XAxis
              dataKey="label"
              tick={{ fill: '#6B7280', fontSize: 11 }}
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: '#6B7280', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={52}
              tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
            />
            <Tooltip
              cursor={{ fill: 'rgba(107,114,128,0.08)' }}
              contentStyle={CHART_TOOLTIP_STYLE}
              formatter={(v: number, name: string) => [money(v), seriesLabel(name)]}
            />
            <Legend
              iconType="circle"
              formatter={(value: string) => seriesLabel(value)}
              wrapperStyle={{ fontSize: 11, color: '#6B7280' }}
            />
            {SERIES.map((s, i) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                stackId="cost"
                fill={s.color}
                radius={i === SERIES.length - 1 ? [4, 4, 0, 0] : undefined}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function SummaryCard({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-0.5 text-lg font-semibold tabular-nums text-gray-900">{value}</p>
      {detail && <p className="text-xs text-gray-500">{detail}</p>}
    </div>
  );
}
